import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { CSSProperties, ReactNode } from 'react';

import type { Participante } from '../api/participantes';
import { formatarMatricula } from '../matricula';

/** As ações que já têm fluxo pronto. Cada incremento acrescenta as suas; item sem ação não aparece. */
export type Acao = 'inativar' | 'reativar' | 'editar' | 'codigo';

export const svg = (conteudo: ReactNode) => (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {conteudo}
    </svg>
);

/** Rótulo e ícone de cada item; o interruptor da situação é o do modelo (`participantes.png`). */
export const ITENS: Record<Acao, { rotulo: string; icone: ReactNode }> = {
    inativar: { rotulo: 'Inativar', icone: <span className="interruptor interruptor-ligado" aria-hidden="true" /> },
    reativar: { rotulo: 'Reativar', icone: <span className="interruptor" aria-hidden="true" /> },
    editar: { rotulo: 'Editar dados', icone: svg(<path d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4" />) },
    codigo: { rotulo: 'Gerar novo código', icone: svg(<><circle cx="8" cy="15" r="4" /><path d="M11 12l9-9M17 6l3 3" /></>) },
};

/**
 * O que o menu oferece (`RN-PT-028`, `RI-PT-015`): a situação primeiro, como no modelo, depois as
 * demais ações prontas.
 */
export function acoesDisponiveis(participante: Participante): Acao[] {
    return [participante.situacao === 'ativo' ? 'inativar' : 'reativar', 'editar', 'codigo'];
}

export interface ItemDoMenu<T extends string = string> {
    chave: T;
    rotulo: string;
    icone: ReactNode;
    /** Item do modelo ainda sem comportamento decidido: aparece, mas não faz nada ("Em breve"). */
    desabilitado?: boolean;
    /** Ação irreversível, em vermelho como no modelo (`img_1.png`). */
    perigo?: boolean;
}

interface MenuSuspensoProps<T extends string> {
    /** Nome acessível do botão "⋯" e do menu que ele abre. */
    rotulo: string;
    itens: ItemDoMenu<T>[];
    aberto: boolean;
    aoAlternar: () => void;
    aoFechar: () => void;
    aoEscolher: (chave: T) => void;
}

/** Espaço entre o botão "⋯" e o menu. */
const FOLGA_PX = 4;

/**
 * Onde o menu flutua: logo abaixo do botão, alinhado pela direita dele; sem espaço embaixo, sobe.
 * É `fixed` na tela inteira porque, dentro da tabela, o `overflow` dela cortava o menu.
 */
function posicaoDoMenu(botao: HTMLElement, lista: HTMLElement | null): CSSProperties {
    const caixa = botao.getBoundingClientRect();
    const altura = lista?.offsetHeight ?? 0;
    const abaixo = caixa.bottom + FOLGA_PX;
    const cabeEmbaixo = abaixo + altura <= window.innerHeight || caixa.top - FOLGA_PX - altura < 0;
    return {
        position: 'fixed',
        top: `${cabeEmbaixo ? abaixo : caixa.top - FOLGA_PX - altura}px`,
        right: `${window.innerWidth - caixa.right}px`,
    };
}

/** O botão "⋯" e o menu dele: comum à tela Participantes e ao modal do grupo (`RI-MA-*`). */
export function MenuSuspenso<T extends string>({ rotulo, itens, aberto, aoAlternar, aoFechar, aoEscolher }: MenuSuspensoProps<T>) {
    const botao = useRef<HTMLButtonElement | null>(null);
    const lista = useRef<HTMLDivElement | null>(null);
    const [posicao, setPosicao] = useState<CSSProperties>({ position: 'fixed' });

    // O menu sai da tabela (portal no `body`) e acompanha o botão se a tela rolar ou mudar de tamanho.
    useLayoutEffect(() => {
        if (!aberto || !botao.current) return undefined;
        const reposicionar = () => {
            if (botao.current) setPosicao(posicaoDoMenu(botao.current, lista.current));
        };
        reposicionar();
        window.addEventListener('scroll', reposicionar, true);
        window.addEventListener('resize', reposicionar);
        return () => {
            window.removeEventListener('scroll', reposicionar, true);
            window.removeEventListener('resize', reposicionar);
        };
    }, [aberto]);

    // Ao abrir, o foco vai para o primeiro item, como num menu de verdade.
    useEffect(() => {
        if (aberto) lista.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    }, [aberto]);

    // Esc fecha sem alterar nada e devolve o foco a quem abriu (`RI-PT-039`).
    useEffect(() => {
        if (!aberto) return undefined;
        function aoTeclar(evento: KeyboardEvent) {
            if (evento.key !== 'Escape') return;
            aoFechar();
            botao.current?.focus();
        }
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [aberto, aoFechar]);

    return (
        <div className="menu-de-acoes">
            <button
                ref={botao}
                type="button"
                className="botao-acoes"
                aria-label={rotulo}
                aria-haspopup="menu"
                aria-expanded={aberto}
                onClick={aoAlternar}
            >
                ⋯
            </button>
            {aberto && createPortal(
                <div ref={lista} className="lista-de-acoes lista-flutuante" role="menu" aria-label={rotulo} style={posicao}>
                    {itens.map((item) => (
                        <button
                            key={item.chave}
                            type="button"
                            role="menuitem"
                            className={item.perigo ? 'item-perigo' : undefined}
                            aria-disabled={item.desabilitado || undefined}
                            title={item.desabilitado ? 'Em breve' : undefined}
                            onClick={() => {
                                if (!item.desabilitado) aoEscolher(item.chave);
                            }}
                        >
                            {item.icone}
                            <span>{item.rotulo}</span>
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </div>
    );
}

interface MenuDeAcoesProps {
    participante: Participante;
    aberto: boolean;
    aoAlternar: () => void;
    aoFechar: () => void;
    aoEscolher: (acao: Acao) => void;
}

export function MenuDeAcoes({ participante, aberto, aoAlternar, aoFechar, aoEscolher }: MenuDeAcoesProps) {
    return (
        <MenuSuspenso
            rotulo={`Ações de ${formatarMatricula(participante.matricula)}`}
            itens={acoesDisponiveis(participante).map((acao) => ({ chave: acao, ...ITENS[acao] }))}
            aberto={aberto}
            aoAlternar={aoAlternar}
            aoFechar={aoFechar}
            aoEscolher={aoEscolher}
        />
    );
}
